import React, { useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  Chip,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Paper,
} from '@mui/material';
import {
  Computer as LocalIcon,
  Cloud as CloudIcon,
  Memory as ChipIcon,
} from '@mui/icons-material';
import {
  ModelConfig,
  getAvailableModels,
  getDefaultTextModel,
  createOllamaModelConfig,
  isOllamaProvider,
  getModelById,
} from '../../config/modelConfig';

interface ModelSelectorProps {
  value: string;
  onChange: (modelId: string) => void;
  disabled?: boolean;
  fullWidth?: boolean;
  showCustomInput?: boolean;
  label?: string;
}

interface GroupedModel {
  group: string;
  models: ModelConfig[];
}

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  fullWidth = true,
  showCustomInput = true,
  label = "AI Model"
}) => {
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [selectedModel, setSelectedModel] = useState<ModelConfig | null>(null);
  const [inputValue, setInputValue] = useState('');

  useEffect(() => {
    // Load available text models
    const textModels = getAvailableModels('text');
    
    // If custom models are enabled, add placeholder for custom entry
    if (showCustomInput) {
      // Models are already in the list, just use them
      setModels(textModels);
    } else {
      setModels(textModels);
    }
    
    // Set initial value
    if (!value) {
      const defaultModelId = getDefaultTextModel();
      const defaultModel = textModels.find(m => m.id === defaultModelId);
      if (defaultModel) {
        setSelectedModel(defaultModel);
        onChange(defaultModelId);
      }
    } else {
      // Find the model for the current value
      let currentModel = textModels.find(m => m.id === value);
      
      // If not found and it's an Ollama model, create a custom config
      if (!currentModel && value) {
        currentModel = createOllamaModelConfig(value);
        setModels([...textModels, currentModel]);
      }
      
      if (currentModel) {
        setSelectedModel(currentModel);
        setInputValue(currentModel.name);
      }
    }
  }, []); // Only run once on mount

  // Update selected model when value prop changes
  useEffect(() => {
    if (value && value !== selectedModel?.id) {
      let model = models.find(m => m.id === value);
      if (!model) {
        model = createOllamaModelConfig(value);
        setModels(prev => [...prev, model!]);
      }
      setSelectedModel(model);
      setInputValue(model.name);
    }
  }, [value]);

  const handleModelChange = (_event: any, newValue: ModelConfig | string | null) => {
    if (typeof newValue === 'string') {
      // User typed a custom value
      if (showCustomInput && newValue.trim()) {
        const customModel = createOllamaModelConfig(newValue);
        setModels(prev => [...prev, customModel]);
        setSelectedModel(customModel);
        onChange(newValue);
      }
    } else if (newValue) {
      // User selected from dropdown
      setSelectedModel(newValue);
      onChange(newValue.id);
    }
  };

  // Group models by provider type
  const groupedOptions = models.reduce((acc: ModelConfig[], model) => {
    return [...acc, model];
  }, []);

  const getProviderIcon = (provider: string) => {
    if (isOllamaProvider(provider)) {
      return <LocalIcon sx={{ fontSize: 20 }} />;
    }
    return <CloudIcon sx={{ fontSize: 20 }} />;
  };

  const getProviderColor = (provider: string) => {
    if (isOllamaProvider(provider)) return 'success';
    if (provider === 'openai') return 'primary';
    if (provider === 'anthropic') return 'secondary';
    if (provider === 'google') return 'info';
    return 'default';
  };

  return (
    <Autocomplete
      value={selectedModel}
      onChange={handleModelChange}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={groupedOptions}
      groupBy={(option) => isOllamaProvider(option.provider) ? 'Local Models (Ollama)' : 'Cloud Models'}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option.name;
      }}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      fullWidth={fullWidth}
      disabled={disabled}
      freeSolo={showCustomInput}
      autoHighlight
      clearOnBlur={false}
      selectOnFocus
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={showCustomInput ? "Select or type a custom model (e.g., gemma3:27b)" : "Select a model"}
          helperText={
            selectedModel 
              ? `Provider: ${selectedModel.provider}${selectedModel.maxTokens ? ` • Max Tokens: ${(selectedModel.maxTokens / 1000).toFixed(0)}k` : ''}`
              : "Choose from available models or enter a custom Ollama model"
          }
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: 'transparent', width: 32, height: 32 }}>
              {getProviderIcon(option.provider)}
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body1">{option.name}</Typography>
                <Chip 
                  label={option.provider} 
                  size="small" 
                  color={getProviderColor(option.provider) as any}
                  variant="outlined"
                  sx={{ height: 20 }}
                />
                {option.maxTokens && (
                  <Chip 
                    label={`${(option.maxTokens / 1000).toFixed(0)}k`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20 }}
                    icon={<ChipIcon sx={{ fontSize: 14 }} />}
                  />
                )}
              </Box>
            }
            secondary={
              option.id !== option.name ? (
                <Typography variant="caption" color="text.secondary">
                  Model ID: {option.id}
                </Typography>
              ) : null
            }
          />
        </Box>
      )}
      renderGroup={(params) => (
        <Box key={params.key}>
          <Box
            sx={{
              position: 'sticky',
              top: -8,
              padding: '8px 16px',
              bgcolor: 'background.paper',
              borderBottom: 1,
              borderColor: 'divider',
              zIndex: 1,
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {params.group.includes('Local') ? <LocalIcon fontSize="small" /> : <CloudIcon fontSize="small" />}
              {params.group}
            </Typography>
          </Box>
          {params.children}
        </Box>
      )}
      PaperComponent={(props) => (
        <Paper {...props} elevation={8} sx={{ mt: 1, minWidth: 500 }} />
      )}
      ListboxProps={{
        sx: {
          maxHeight: 400,
          minWidth: 500,
          '& .MuiAutocomplete-option': {
            padding: '8px 16px',
          },
        },
      }}
      sx={{
        '& .MuiAutocomplete-inputRoot': {
          paddingRight: '39px !important',
        },
      }}
    />
  );
};

export default ModelSelector;